import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  BedDouble, 
  MessageSquare, 
  Calendar, 
  Star, 
  Tag, 
  Mail, 
  LogOut,
  Home,
  Menu,
  X,
  FileText,
  Settings,
  MapPin,
  CalendarCheck,
  Image,
  Users,
  Navigation,
  File,
  ChevronDown,
  Shield,
  Hotel,
  UtensilsCrossed
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// Menu structure grouped by category with role permissions
const menuGroups = [
  {
    id: 'overview',
    label: null,
    items: [
      { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'content', 'manager'] },
    ]
  },
  {
    id: 'content',
    label: 'Content',
    roles: ['admin', 'content'],
    items: [
      { path: '/admin/pages', icon: File, label: 'Pages', roles: ['admin', 'content'] },
      { path: '/admin/menu', icon: Navigation, label: 'Navigation', roles: ['admin', 'content'] },
      { path: '/admin/blog', icon: FileText, label: 'Blog', roles: ['admin', 'content'] },
      { path: '/admin/gallery', icon: Image, label: 'Gallery', roles: ['admin', 'content'] },
      { path: '/admin/dining', icon: UtensilsCrossed, label: 'Dining', roles: ['admin', 'content'] },
      { path: '/admin/testimonials', icon: Star, label: 'Testimonials', roles: ['admin', 'content'] },
      { path: '/admin/offers', icon: Tag, label: 'Offers', roles: ['admin', 'content'] },
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    roles: ['admin', 'manager'],
    items: [
      { path: '/admin/rooms', icon: BedDouble, label: 'Rooms', roles: ['admin', 'manager'] },
      { path: '/admin/booking-inquiries', icon: CalendarCheck, label: 'Bookings', roles: ['admin', 'manager'] },
      { path: '/admin/trip-leads', icon: MapPin, label: 'Trip Leads', roles: ['admin', 'manager'] },
      { path: '/admin/inquiries', icon: MessageSquare, label: 'Inquiries', roles: ['admin', 'manager'] },
      { path: '/admin/events', icon: Calendar, label: 'Events', roles: ['admin', 'manager'] },
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    roles: ['admin'],
    items: [
      { path: '/admin/users', icon: Users, label: 'Users', roles: ['admin'] },
      { path: '/admin/settings', icon: Settings, label: 'Site Settings', roles: ['admin'] },
      { path: '/admin/newsletter', icon: Mail, label: 'Newsletter', roles: ['admin'] },
    ]
  },
];

const roleColors = {
  admin: 'bg-red-500',
  content: 'bg-blue-500',
  manager: 'bg-emerald-500'
};

const roleLabels = {
  admin: 'Admin',
  content: 'Content',
  manager: 'Manager'
};

export function AdminSidebar() {
  const { admin, logout, role } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const userRole = role || admin?.role || 'content';

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const canAccess = (itemRoles) => {
    if (userRole === 'admin') return true;
    return itemRoles.includes(userRole);
  };

  const canAccessGroup = (group) => {
    if (userRole === 'admin') return true;
    if (!group.roles) return true;
    return group.roles.includes(userRole);
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-emerald-800">
        <h1 className="font-serif text-xl text-white">Hotel Emerald</h1>
        <p className="text-emerald-400 text-xs mt-1">Content Management</p>
      </div>

      <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
        {menuGroups.map((group) => {
          if (!canAccessGroup(group)) return null;
          
          const visibleItems = group.items.filter(item => canAccess(item.roles));
          if (visibleItems.length === 0) return null;

          return (
            <div key={group.id}>
              {group.label && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="flex items-center justify-between w-full px-4 py-2 text-xs font-semibold text-emerald-400 uppercase tracking-wider"
                >
                  {group.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${collapsedGroups[group.id] ? '-rotate-90' : ''}`} />
                </button>
              )}
              
              {!collapsedGroups[group.id] && (
                <div className="space-y-1">
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                          isActive
                            ? 'bg-emerald-700 text-white'
                            : 'text-emerald-300 hover:bg-emerald-800 hover:text-white'
                        }`
                      }
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-emerald-800">
        <div className="px-4 py-2 mb-3">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white text-sm font-medium">{admin?.full_name || admin?.username}</p>
            <Badge className={`${roleColors[userRole]} text-white text-[10px] px-1.5 py-0`}>
              {roleLabels[userRole]}
            </Badge>
          </div>
          <p className="text-emerald-400 text-xs">{admin?.email}</p>
        </div>
        
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-emerald-300 hover:bg-emerald-800 hover:text-white transition-colors text-sm"
        >
          <Home className="w-4 h-4" />
          <span>View Website</span>
        </a>
        
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-400 hover:bg-red-900/30 hover:text-red-300 transition-colors w-full text-sm"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden bg-emerald-900 text-white hover:bg-emerald-800"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-emerald-950 flex flex-col z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <SidebarContent />
      </aside>
    </>
  );
}
